import { ReactNode, useEffect, useMemo } from 'react';
import classNames from 'classnames';

import { useScreenSize } from '@hcs/hooks';

import { useCardTabsState } from '../../../../hooks/useCardTabsState';
import { Dropdown } from '../../../controls-and-inputs';
import { TabButton } from '../TabButton';

import styles from './CardTabs.module.css';

export interface Tab {
  tabId: string;
  noPadding?: boolean;
  dataHcEventSection?: string;
  label: ReactNode;
  content: ReactNode;
  scrollable?: boolean;
  onClick?: (tabId: string) => void;
}

interface CardTabsTheme {
  ActiveTabLabel: string;
  ActiveTabLabelText: string;
  TabButton: string;
  Content: string;
  lastSelected: string;
}

interface ActionTextConfig {
  tabId: string;
  content: ReactNode;
}
export interface CardTabsProps {
  dataHcName: string;
  /** For searchParam active tab */
  searchParamId: string;
  className?: string;
  theme?: Partial<CardTabsTheme>;
  actionTextConfig?: ActionTextConfig;
  tabs: (Tab | null)[];
  active?: string;
  activeInitial?: string;
  maxWidth?: number;
  minWidth?: number;
  flexTabs?: boolean;
  minContentHeight?: number | string;
  useCompactPadding?: boolean;
  ariaLabel?: string;
  dropdownOnPhone?: boolean;
}
export const CardTabs = ({
  searchParamId,
  active,
  dataHcName,
  theme,
  actionTextConfig,
  className,
  minWidth,
  maxWidth,
  tabs,
  flexTabs,
  minContentHeight,
  activeInitial,
  useCompactPadding,
  ariaLabel,
  dropdownOnPhone,
}: CardTabsProps) => {
  const { isPhoneWidth } = useScreenSize();
  const {
    state: { activeTabId: activeTabIdState },
    actions: { setActiveTabId },
  } = useCardTabsState(searchParamId, activeInitial || active);
  const activeTabId = activeTabIdState
    ? activeTabIdState
    : tabs?.[0]
      ? tabs[0].tabId
      : null;
  const activeTab = useMemo(() => {
    return tabs.find((t) => t?.tabId && t.tabId === activeTabId);
  }, [activeTabId, tabs]);
  useEffect(() => {
    if (!activeTab) {
      if (tabs?.[0]) {
        setActiveTabId(tabs[0].tabId);
      }
    }
  }, [activeTab]);
  useEffect(() => {
    if (active) {
      setActiveTabId(active);
    }
  }, [active]);

  return (
    <section
      data-hc-name={dataHcName}
      aria-label={ariaLabel}
      className={classNames(className, styles.CardTabs, {
        [styles.dropdownOnPhone]: dropdownOnPhone,
      })}
      style={{ minWidth, maxWidth }}
    >
      <div
        className={classNames(styles.Tabs, {
          [styles.flexTabs]: flexTabs,
          [styles.MobileDropdown]: dropdownOnPhone && isPhoneWidth,
        })}
        role="tablist"
      >
        {dropdownOnPhone && isPhoneWidth ? (
          <Dropdown
            dataHcName={`${dataHcName}-mobile-dropdown`}
            value={activeTab?.tabId}
            isFloatingStyle
            options={tabs.map((t) => ({
              label: t?.label,
              value: t?.tabId || '',
            }))}
            onSelect={(tabId) => {
              const tab = tabs.find((t) => t?.tabId === tabId);
              if (tab) {
                tab.onClick?.(tab.tabId);
                setActiveTabId(tab.tabId);
              }
            }}
          />
        ) : (
          tabs?.map((t, i) => {
            if (!t) return null;
            const isActiveTab = t.tabId === activeTabId;
            return (
              <TabButton
                dataHcName={`${dataHcName}-${t.tabId}`}
                dataHcEventSection={t.dataHcEventSection}
                onClick={() => {
                  t.onClick?.(t.tabId);
                  setActiveTabId(t.tabId);
                }}
                key={`tab-${t.tabId}`}
                isActive={isActiveTab}
                tabIndex={i}
                label={t.label}
                curveRight={
                  flexTabs && i === tabs.length - 1 ? false : undefined
                }
                theme={{
                  Label: theme?.ActiveTabLabelText,
                  TabButton: theme?.TabButton,
                }}
              />
            );
          })
        )}
      </div>
      <div
        data-hc-name={`${dataHcName}-content`}
        style={minContentHeight ? { minHeight: minContentHeight } : undefined}
        className={classNames(styles.Content, theme?.Content, {
          [styles.firstSelected]: tabs?.[0] && tabs[0].tabId === activeTabId,
          [styles.lastSelected]:
            tabs[tabs.length - 1]?.tabId === activeTabId && flexTabs,
          [theme?.lastSelected || '']:
            tabs[tabs.length - 1]?.tabId === activeTabId && flexTabs,
          [useCompactPadding ? styles.CompactPadding : styles.ContentPadding]:
            activeTab?.noPadding !== true,
          [theme?.ActiveTabLabel || '']: activeTab,
          [styles.scrollingContent]: activeTab?.scrollable,
        })}
      >
        {actionTextConfig &&
          activeTabId === actionTextConfig.tabId &&
          actionTextConfig.content}
        {activeTab?.content}
      </div>
    </section>
  );
};
